import {
    DEFAULT_AUTH_API_URL,
    getAuthApiBaseFromHost,
    isLessonPath,
    isPlinkromaticApprovedLessonHeaderPath,
} from 'helpers';
import { useAnalytics } from 'contexts/Analytics/Analytics';
import { useRouter } from 'next/router';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ApiContext = createContext<ApiState>({} as ApiState);
const AUTH_TOKEN_KEY = 'kromatic-user-t';
const GAME_SLUG = 'plinkromatic';

export const useApi = () => useContext(ApiContext);

export function withApi(WrappedComponent: any) {
    return function ApiWrappedComponent(props: any) {
        return <ApiContext.Consumer>{state => <WrappedComponent {...props} api={state} />}</ApiContext.Consumer>;
    };
}

export function ApiProvider({ children }: ApiProps) {
    const [authToken, setAuthToken] = useState<string | null>(null);
    const [authModalScreen, setAuthModalScreen] = useState<ApiAuthModalScreen>(null);
    const [loading, setLoading] = useState(false);
    const [progressLoading, setProgressLoading] = useState(false);
    const [resumeLesson, setResumeLesson] = useState<string | null>(null);
    const [ready, setReady] = useState(false);
    const [userInfo, setUserInfo] = useState<ApiUserInfo | null>(null);
    const { trackActionEvent, trackLeadEvent, identify, clearIdentity } = useAnalytics();
    const router = useRouter();

    const authApiUrl = useMemo(() => {
        if (typeof window !== 'undefined') {
            const fromHost = getAuthApiBaseFromHost(window.location.hostname);
            if (fromHost) return fromHost;
        }
        return process.env.NEXT_PUBLIC_AUTH_API_URL || process.env.NEXT_PUBLIC_BASE_URL || DEFAULT_AUTH_API_URL;
    }, []);

    const normalizeToken = useCallback((token: string | null | undefined) => {
        if (!token) {
            return '';
        }

        return token.replace(/^bearer\s+/i, '').trim();
    }, []);

    const normalizeCheckpointId = useCallback((checkpointId: unknown) => {
        if (typeof checkpointId !== 'string') {
            return null;
        }

        const normalized = checkpointId.trim();
        if (!normalized) {
            return null;
        }

        return /^\d+(?:\.\d+)*$/.test(normalized) ? normalized : null;
    }, []);

    const clearAuthState = useCallback(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.removeItem(AUTH_TOKEN_KEY);
            // Drop the persisted auid so a stale identity can't outlive the
            // session via the pre-bootstrap re-push (RSB#735).
            window.localStorage.removeItem('kromatic-user-auid');
        }
        setAuthToken(null);
        setResumeLesson(null);
        setUserInfo(null);
        // Clear the GA4 user_id so a stale identity can't outlive the session (#481).
        clearIdentity();
    }, [clearIdentity]);

    const fetchProgress = useCallback(
        async (token: string) => {
            const normalizedToken = normalizeToken(token);

            setProgressLoading(true);
            try {
                const response = await fetch(`${authApiUrl}/kromatic/user/game-progress/${GAME_SLUG}`, {
                    headers: {
                        Authorization: `Bearer ${normalizedToken}`,
                    },
                    method: 'GET',
                });

                if (!response.ok) {
                    setResumeLesson(null);
                    return;
                }

                const data = await response.json();
                const checkpointLesson = normalizeCheckpointId(data?.last_checkpoint_id);
                setResumeLesson(checkpointLesson);
            } catch {
                setResumeLesson(null);
            } finally {
                setProgressLoading(false);
            }
        },
        [authApiUrl, normalizeCheckpointId, normalizeToken]
    );

    const fetchUserInfo = useCallback(
        async (token: string) => {
            const normalizedToken = normalizeToken(token);
            if (!normalizedToken) {
                return;
            }
            try {
                const response = await fetch(`${authApiUrl}/user/info/`, {
                    headers: {
                        Authorization: `Bearer ${normalizedToken}`,
                        accept: 'application/json',
                    },
                    method: 'GET',
                });
                if (!response.ok) {
                    return;
                }
                const data = await response.json();
                const first_name = data?.first_name ?? data?.firstName ?? null;
                const avatar = data?.avatar ?? data?.profile_image ?? null;
                if (first_name != null || avatar != null) {
                    setUserInfo({ first_name: first_name ?? undefined, avatar: avatar ?? undefined });
                }
                // Push the salted-HMAC auid pseudonym as the GA4 user_id (#481).
                // Never the raw id/email/name. Re-fires on every authed load.
                const auid = typeof data?.auid === 'string' && data.auid ? data.auid : null;
                if (auid) {
                    identify(auid);
                    // Persist the auid so the Analytics provider can re-push
                    // set_user_id BEFORE GTM bootstrap on the next authed load
                    // (RSB#735). Salted-HMAC pseudonym only — never raw id/email/name.
                    if (typeof window !== 'undefined') {
                        window.localStorage.setItem('kromatic-user-auid', auid);
                    }
                } else {
                    // A successful /user/info/ with no auid means this session has
                    // no analytics identity. Clear any auid persisted by a prior
                    // user/session so the pre-bootstrap re-push can't resurrect a
                    // stale identity on the next load (RSB#735).
                    if (typeof window !== 'undefined') {
                        window.localStorage.removeItem('kromatic-user-auid');
                    }
                    clearIdentity();
                }
            } catch {
                // Best-effort: leave userInfo null if endpoint missing or fails
            }
        },
        [authApiUrl, clearIdentity, identify, normalizeToken]
    );

    const syncAuthToken = useCallback(
        async (token: string) => {
            const normalizedToken = normalizeToken(token);

            if (typeof window !== 'undefined') {
                window.localStorage.setItem(AUTH_TOKEN_KEY, normalizedToken);
            }

            setAuthToken(normalizedToken);
            await fetchProgress(normalizedToken);
            void fetchUserInfo(normalizedToken);
            return normalizedToken;
        },
        [fetchProgress, fetchUserInfo, normalizeToken]
    );

    useEffect(() => {
        let active = true;

        async function initializeAuth() {
            /* istanbul ignore next */
            if (typeof window === 'undefined') {
                if (active) {
                    setReady(true);
                }
                return;
            }

            const storedToken = normalizeToken(window.localStorage.getItem(AUTH_TOKEN_KEY));
            if (storedToken) {
                setAuthToken(storedToken);
                await fetchProgress(storedToken);
                await fetchUserInfo(storedToken);
            } else {
                setResumeLesson(null);
            }

            if (active) {
                setReady(true);
            }
        }

        void initializeAuth();

        return () => {
            active = false;
        };
    }, [fetchProgress, fetchUserInfo, normalizeToken]);

    useEffect(() => {
        if (!ready) {
            return;
        }

        // Redirect lesson deep-links to landing when registration is enabled and the user is not authenticated yet.
        if (
            isLessonPath(router.asPath) &&
            !isPlinkromaticApprovedLessonHeaderPath(router.asPath) &&
            process.env.NEXT_PUBLIC_REGISTRATION &&
            !authToken
        ) {
            router.replace('/');
        }
    }, [authToken, ready, router]);

    useEffect(() => {
        /* istanbul ignore next */
        if (typeof window === 'undefined') {
            return;
        }

        function handleStorage(event: StorageEvent) {
            if (event.key !== AUTH_TOKEN_KEY) {
                return;
            }

            const nextToken = normalizeToken(event.newValue);
            if (!nextToken) {
                setAuthToken(null);
                setResumeLesson(null);
                return;
            }

            setAuthToken(nextToken);
            void fetchProgress(nextToken);
            void fetchUserInfo(nextToken);
        }

        window.addEventListener('storage', handleStorage);
        return () => {
            window.removeEventListener('storage', handleStorage);
        };
    }, [fetchProgress, fetchUserInfo, normalizeToken]);

    async function login(payload: ApiLoginPayload) {
        setLoading(true);

        try {
            const response = await fetch(`${authApiUrl}/auth/login`, {
                body: JSON.stringify(payload),
                headers: {
                    'content-type': 'application/json',
                },
                method: 'POST',
            });
            const data = await response.json();
            const token = normalizeToken(data?.access_token);
            if (!response.ok || !token) {
                throw new Error(data?.message || 'Unable to login');
            }

            await syncAuthToken(token);
            setAuthModalScreen(null);
            return data;
        } finally {
            setLoading(false);
        }
    }

    async function join(payload: ApiJoinPayload) {
        setLoading(true);
        void trackActionEvent('action_signup_started', {
            auth_state: 'guest',
            resource_type: 'tool',
            source: 'landing_registration',
        });

        try {
            const response = await fetch(`${authApiUrl}/auth/registration`, {
                body: JSON.stringify(payload),
                headers: {
                    'content-type': 'application/json',
                },
                method: 'POST',
            });
            const data = await response.json();
            const token = normalizeToken(data?.access_token);
            if (!response.ok || !token) {
                throw new Error(data?.message || 'Unable to join');
            }

            await syncAuthToken(token);
            setAuthModalScreen(null);
            void trackActionEvent('action_signup_completed', {
                auth_state: 'member',
                resource_type: 'tool',
                source: 'landing_registration',
            });
            void trackLeadEvent('lead_soft_signup_completed', {
                auth_state: 'member',
                consent_newsletter: payload.subscribe_to_news,
                resource_type: 'tool',
                source: 'landing_registration',
            });
            if (payload.subscribe_to_news) {
                void trackLeadEvent('lead_soft_newsletter_subscribed', {
                    auth_state: 'member',
                    resource_type: 'newsletter',
                    source: 'registration_opt_in',
                    consent_newsletter: true,
                });
            }

            return data;
        } catch (error) {
            void trackActionEvent('action_signup_failed', {
                auth_state: 'guest',
                resource_type: 'tool',
                source: 'landing_registration',
            });
            throw error;
        } finally {
            setLoading(false);
        }
    }

    async function logout() {
        setLoading(true);

        try {
            if (authToken) {
                await fetch(`${authApiUrl}/auth/logout`, {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                    method: 'POST',
                });
            }
        } catch {
            // Do not block local logout when backend logout fails.
        } finally {
            clearAuthState();
            setLoading(false);
            if (isLessonPath(router.asPath)) {
                router.replace('/');
            }
        }
    }

    async function saveCheckpoint(payload: ApiProgressPayload) {
        const token = normalizeToken(authToken);
        if (!token) {
            return;
        }

        try {
            await fetch(`${authApiUrl}/kromatic/user/game-progress/${GAME_SLUG}`, {
                body: JSON.stringify(payload),
                headers: {
                    Authorization: `Bearer ${token}`,
                    'content-type': 'application/json',
                },
                method: 'PUT',
            });
            setResumeLesson(normalizeCheckpointId(payload.last_checkpoint_id));
        } catch {
            // Checkpoint save failure should not block gameplay.
        }
    }

    function startGoogleOAuth(mode: 'login' | 'join', subscribeToNews = false) {
        /* istanbul ignore next */
        if (typeof document === 'undefined' || typeof window === 'undefined') {
            return;
        }

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${authApiUrl}/auth/login/google`;
        form.style.display = 'none';

        const returnUrlInput = document.createElement('input');
        returnUrlInput.type = 'hidden';
        returnUrlInput.name = 'return_url';
        returnUrlInput.value = `${window.location.pathname}${window.location.search}`;
        form.appendChild(returnUrlInput);

        const newsletterInput = document.createElement('input');
        newsletterInput.type = 'hidden';
        newsletterInput.name = 'subscribe_to_news';
        newsletterInput.value = mode === 'join' && subscribeToNews ? 'true' : 'false';
        form.appendChild(newsletterInput);

        document.body.appendChild(form);
        form.submit();
    }

    function showAuthModal(screen: Exclude<ApiAuthModalScreen, null> = 'login') {
        setAuthModalScreen(screen);
    }

    function hideAuthModal() {
        setAuthModalScreen(null);
    }

    const authEnabled = useMemo(() => !!process.env.NEXT_PUBLIC_REGISTRATION, []);

    const state = {
        authEnabled,
        authToken,
        authModalScreen,
        isAuthenticated: !!normalizeToken(authToken),
        loading,
        progressLoading,
        resumeLesson,
        userInfo,
        hideAuthModal,
        join,
        login,
        logout,
        saveCheckpoint,
        showAuthModal,
        startGoogleOAuth,
    };

    return <ApiContext.Provider value={state}>{ready && children}</ApiContext.Provider>;
}
